import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", sku: "", unit: "個", unit_price: 0, tax_rate: 10, tax_category: "課税", description: "" };

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    api.get("/billing/products").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("商品名を入力してください"); return; }
    try {
      const payload = { ...form, unit_price: Number(form.unit_price), tax_rate: Number(form.tax_rate) };
      if (editing) await api.put(`/billing/products/${editing.id}`, payload);
      else await api.post("/billing/products", payload);
      toast.success("保存しました"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`${p.name} を削除しますか？`)) return;
    try { await api.delete(`/billing/products/${p.id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="billing-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-stone-900">商品・サービス</h1>
          <p className="text-sm text-stone-500 mt-0.5">{items.length} 件</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700" data-testid="new-product-button">
          <Plus className="h-4 w-4" /> 商品を追加
        </button>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">商品名</th>
              <th className="px-4 py-3 text-left">品番</th>
              <th className="px-4 py-3 text-left">単位</th>
              <th className="px-4 py-3 text-right">単価</th>
              <th className="px-4 py-3 text-left">税率</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400"><Package className="h-6 w-6 inline mb-2 opacity-40" /><div>商品はまだありません</div></td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="hover:bg-stone-50/60" data-testid={`product-row-${p.id}`}>
                <td className="px-4 py-3 font-medium text-stone-900 cursor-pointer" onClick={() => openEdit(p)}>{p.name}</td>
                <td className="px-4 py-3 text-stone-500">{p.sku || "—"}</td>
                <td className="px-4 py-3 text-stone-500">{p.unit}</td>
                <td className="px-4 py-3 text-right">{formatJPY(p.unit_price)}</td>
                <td className="px-4 py-3">{p.tax_rate}%</td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(p)} className="text-stone-400 hover:text-red-500" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing ? "商品を編集" : "商品を追加"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>商品名</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>品番</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              <div><Label>単位</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>単価</Label><Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="product-price" /></div>
              <div>
                <Label>税率</Label>
                <Select value={String(form.tax_rate)} onValueChange={(v) => setForm({ ...form, tax_rate: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="10">10%</SelectItem><SelectItem value="8">8%（軽減）</SelectItem><SelectItem value="0">0%</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>説明</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={save} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700" data-testid="product-save-button">保存</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
